import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink, Loader2, Check, Image, RefreshCw, Edit2, X, Save } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import nexisLogo from "@/assets/nexis-logo-new.png";
import { WhatsNextSection } from "@/components/nexis/WhatsNextSection";
import { useCredits } from "@/hooks/useCredits";
import { NexisBreadcrumb } from "@/components/nexis/NexisBreadcrumb";

interface Scene {
  id: string;
  scene_number: number;
  duration: string;
  description: string;
  video_prompt: string;
}

interface Project {
  id: string;
  title: string;
  recommended_model: string | null;
  secondary_model: string | null;
  visual_style: string | null;
  visual_style_category: string | null;
  script: string | null;
  platform: string | null;
}

// Video model recommendations based on style
const VIDEO_MODELS: Record<string, { primary: string; secondary: string }> = {
  "Anime": { primary: "Runway Gen-3 Alpha", secondary: "Kling AI" },
  "Photography": { primary: "Runway Gen-3 Alpha", secondary: "Pika Labs" },
  "Vibrant Fantasy": { primary: "Kling AI", secondary: "Runway Gen-3 Alpha" },
  "Comic & Cartoon": { primary: "Pika Labs", secondary: "Runway Gen-3 Alpha" },
  "Gaming": { primary: "Runway Gen-3 Alpha", secondary: "Kling AI" },
  default: { primary: "Runway Gen-3 Alpha", secondary: "Kling AI" },
};

export default function NexisVideoPrompts() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { consume } = useCredits();
  
  const [project, setProject] = useState<Project | null>(null);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState("");
  const [editPrompt, setEditPrompt] = useState("");
  
  const [videoModel, setVideoModel] = useState({ primary: "", secondary: "" });

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectRes, scenesRes] = await Promise.all([
        supabase
          .from("nexis_projects")
          .select("id, title, recommended_model, secondary_model, visual_style, visual_style_category, script, platform")
          .eq("id", projectId)
          .single(),
        supabase
          .from("nexis_scenes")
          .select("*")
          .eq("project_id", projectId)
          .order("scene_number", { ascending: true }),
      ]);

      if (projectRes.error) throw projectRes.error;
      if (scenesRes.error) throw scenesRes.error;
      
      setProject(projectRes.data);
      setScenes(scenesRes.data || []);
      
      // Set video model based on style category
      const category = projectRes.data.visual_style_category || "default";
      setVideoModel(VIDEO_MODELS[category] || VIDEO_MODELS.default);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load prompts");
      navigate("/app/nexis-ai");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPrompt = async (id: string, prompt: string) => {
    await navigator.clipboard.writeText(prompt);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Prompt copied!");
  };

  const handleCopyAll = async () => {
    const allPrompts = scenes.map((s) => `Scene ${s.scene_number}:\n${s.video_prompt}`).join("\n\n");
    await navigator.clipboard.writeText(allPrompts);
    toast.success("All prompts copied!");
  };

  const handleRegenerateScene = async (scene: Scene) => {
    if (!project) return;
    setRegeneratingId(scene.id);
    
    try {
      const credited = await consume("generate-video-prompts", "Regenerate video prompt");
      if (!credited) { setRegeneratingId(null); return; }
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "regenerate-video-scene",
          sceneNumber: scene.scene_number,
          currentDescription: scene.description,
          script: project.script,
          platform: project.platform,
          visualStyle: project.visual_style,
          styleCategory: project.visual_style_category,
        },
      });

      if (error) throw error;

      const result = typeof data.result === "string" ? JSON.parse(data.result) : data.result;

      // Update local state
      setScenes((prev) =>
        prev.map((s) =>
          s.id === scene.id
            ? { ...s, description: result.description || s.description, video_prompt: result.prompt }
            : s
        )
      );

      // Update database
      await supabase
        .from("nexis_scenes")
        .update({
          description: result.description || scene.description,
          video_prompt: result.prompt,
        })
        .eq("id", scene.id);

      toast.success("Video prompt regenerated!");
    } catch (error) {
      console.error("Error regenerating scene:", error);
      toast.error("Failed to regenerate prompt");
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleStartEdit = (scene: Scene) => {
    setEditingId(scene.id);
    setEditDescription(scene.description || "");
    setEditPrompt(scene.video_prompt || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditDescription("");
    setEditPrompt("");
  };

  const handleSaveEdit = async (sceneId: string) => {
    try {
      await supabase
        .from("nexis_scenes")
        .update({
          description: editDescription,
          video_prompt: editPrompt,
        })
        .eq("id", sceneId);

      setScenes((prev) =>
        prev.map((s) =>
          s.id === sceneId ? { ...s, description: editDescription, video_prompt: editPrompt } : s
        )
      );

      setEditingId(null);
      toast.success("Scene updated!");
    } catch (error) {
      console.error("Error saving scene:", error);
      toast.error("Failed to save changes");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <NexisBreadcrumb projectTitle={project?.title} currentStep="Video Prompts" />
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <img src={nexisLogo} alt="Nexis AI" className="w-10 h-10 object-contain" />
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold">Video Prompts</h1>
            <p className="text-sm text-muted-foreground">{project?.title}</p>
          </div>
          <Button variant="outline" onClick={handleCopyAll}>
            <Copy className="w-4 h-4 mr-2" />
            Copy All
          </Button>
        </div>

        {/* Recommended Models */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card className="p-5 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/20">
            <p className="text-sm text-muted-foreground mb-1">Recommended Video Model</p>
            <h3 className="text-lg font-bold text-purple-400">{videoModel.primary}</h3>
            <Button variant="outline" size="sm" className="mt-3" asChild>
              <a href="https://openart.ai/home/?via=fint" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in OpenArt
              </a>
            </Button>
          </Card>
          
          <Card className="p-5 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/20">
            <p className="text-sm text-muted-foreground mb-1">Secondary Model (Alternative)</p>
            <h3 className="text-lg font-bold text-blue-400">{videoModel.secondary}</h3>
            <Button variant="outline" size="sm" className="mt-3" asChild>
              <a href="https://openart.ai/home/?via=fint" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in OpenArt
              </a>
            </Button>
          </Card>
        </div>

        {/* Scene Cards */}
        <div className="space-y-4 mb-8">
          {scenes.map((scene) => (
            <Card key={scene.id} className="p-5 bg-card/50 border-border/50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-sm bg-purple-500/10 text-purple-400 border-purple-500/20">
                    Scene {scene.scene_number}
                  </Badge>
                  <span className="text-sm text-muted-foreground">{scene.duration}</span>
                </div>
                <div className="flex items-center gap-1">
                  {editingId === scene.id ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => handleSaveEdit(scene.id)}>
                        <Save className="w-4 h-4 text-green-500" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                        <X className="w-4 h-4 text-destructive" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRegenerateScene(scene)}
                        disabled={regeneratingId === scene.id}
                      >
                        {regeneratingId === scene.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleStartEdit(scene)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyPrompt(scene.id, scene.video_prompt || "")}
                      >
                        {copiedId === scene.id ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {editingId === scene.id ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Description</label>
                    <Textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      className="min-h-[60px] text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Video Prompt</label>
                    <Textarea
                      value={editPrompt}
                      onChange={(e) => setEditPrompt(e.target.value)}
                      className="min-h-[100px] text-sm font-mono"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mb-3">{scene.description}</p>
                  <div className="bg-background/50 rounded-lg p-3">
                    <p className="text-sm font-mono">{scene.video_prompt}</p>
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>

        {/* What's Next Section */}
        <WhatsNextSection projectId={projectId || ""} />

        {/* Back to Image Prompts */}
        <Button
          variant="outline"
          size="lg"
          className="w-full gap-2 mt-6"
          onClick={() => navigate(`/app/nexis-ai/project/${projectId}/image-prompts`)}
        >
          <Image className="w-5 h-5" />
          Back to Image Prompts
        </Button>
      </div>
    </div>
  );
}
